// Unit 02 — OSI and TCP/IP Models (Lesson + guided activity + lab verification)
import { h } from '../../ui/h.js';
import { sorter, observe, readConfirm } from '../../ui/widgets.js';
import { branch, branchLayout } from './topologies.js';
import { ran, pinged, resolved, fetched, sameMac, macCisco } from './helpers.js';

/** Encapsulation builder: stack headers in the right order as PC1 sends an HTTP GET. */
function encapBuilder() {
  const PARTS = [
    { id: 'l7', label: 'HTTP data: GET / HTTP/1.1  Host: www.example.com', layer: 'Application (L7)', cls: 'l7', pdu: 'Data' },
    { id: 'l4', label: 'TCP header: src 50122 → dst 80, SYN/ACK flags, seq', layer: 'Transport (L4)', cls: 'l4', pdu: 'Segment' },
    { id: 'l3', label: 'IPv4 header: 192.168.10.10 → 93.184.216.34, TTL 128', layer: 'Network (L3)', cls: 'l3', pdu: 'Packet' },
    { id: 'l2', label: 'Ethernet header: dst 0001.4209.a100 (R1) ← src 0050.7966.6800 + FCS', layer: 'Data Link (L2)', cls: 'l2', pdu: 'Frame' },
    { id: 'l1', label: 'Electrical signals on Cat6 pairs to SW1 Fa0/1', layer: 'Physical (L1)', cls: 'l1', pdu: 'Bits' },
  ];
  const WHY = {
    l4: 'Transport comes right after the application hands over its data — it adds ports so the right process receives it.',
    l3: 'IP addressing wraps the segment. Can you address a packet before there is a segment to carry?',
    l2: 'The frame is built last before transmission: it needs the next-hop MAC, which depends on the IP decision.',
    l1: 'Bits are the very last step — the NIC transmits the finished frame.',
    l7: 'Everything starts with what the application wants to send.',
  };
  const shuffled = ['l2', 'l7', 'l1', 'l4', 'l3'];
  return {
    id: 'u02-encap', blocker: 'Finish the encapsulation builder',
    verify: (d) => Array.isArray(d?.built) && d.built.join() === PARTS.map((p) => p.id).join(),
    mount(el, rt, api) {
      const s = api.state.data;
      s.built = s.built || [];
      const render = (msg) => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, 'ENCAPSULATION BUILDER · PC1 → http://www.example.com', api.state.done ? h('span', { class: 'ok' }, '✓ COMPLETE') : null));
        el.append(h('p', { class: 'small muted' }, 'Build what PC1 puts on the wire, starting from the application. Click the next piece that gets added.'));
        const pdu = h('div', { class: 'pdu', 'aria-label': 'Protocol data unit' });
        const order = ['l2', 'l3', 'l4', 'l7'];
        for (const id of order) if (s.built.includes(id)) pdu.append(h('span', { class: PARTS.find((p) => p.id === id).cls }, PARTS.find((p) => p.id === id).layer.split(' ')[0]));
        if (s.built.includes('l2')) pdu.append(h('span', { class: 'fcs' }, 'FCS'));
        el.append(pdu);
        const stack = h('div', { class: 'stack' });
        for (const id of s.built) { const p = PARTS.find((x) => x.id === id); stack.append(h('div', { class: 'layer' }, h('span', {}, `${p.layer}`), h('span', { class: 'muted' }, `${p.pdu}`))); }
        el.append(stack);
        if (!api.state.done) {
          el.append(h('div', { class: 'opt-row' }, shuffled.filter((id) => !s.built.includes(id)).map((id) => h('button', { class: 'opt', 'data-testid': `encap-${id}`, onclick: () => {
            const expected = PARTS[s.built.length].id;
            if (id === expected) {
              s.built.push(id);
              if (s.built.length === PARTS.length) { rt.action('encap', { ok: true }); api.done(); }
              api.save(); render();
            } else render(`Not yet — ${WHY[id]}`);
          } }, PARTS.find((p) => p.id === id).label))));
        }
        if (msg) el.append(h('div', { class: 'w-fb bad' }, msg));
        if (api.state.done) el.append(h('div', { class: 'w-fb good' }, 'Data → Segment → Packet → Frame → Bits. The receiver removes them in reverse (de-encapsulation). Routers look at L3 and rebuild L2 at every hop; switches only look at L2.'));
      };
      render();
    },
  };
}

export default {
  id: 'u02', num: '02', title: 'OSI and TCP/IP Models', type: 'Lesson', implemented: true,
  objective: 'Use the OSI and TCP/IP models as a troubleshooting map: know what each layer adds, which devices work at which layer, and where to look first.',
  network: () => branch(), layout: branchLayout, linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'Two models, one purpose', workspace: false,
      content: `
<p>The models are not trivia — they are the checklist you walk when something breaks. Each layer has one job and adds its own header.</p>
<table>
<tr><th>OSI</th><th>TCP/IP</th><th>PDU</th><th>Job</th><th>Examples</th><th>Devices</th></tr>
<tr><td>7 Application<br>6 Presentation<br>5 Session</td><td>Application</td><td>Data</td><td>What the user/app wants</td><td>HTTP, DNS, DHCP, SSH, TLS</td><td>Servers, endpoints</td></tr>
<tr><td>4 Transport</td><td>Transport</td><td>Segment / Datagram</td><td>Process-to-process: ports, reliability</td><td>TCP, UDP</td><td>Firewalls (L4)</td></tr>
<tr><td>3 Network</td><td>Internet</td><td>Packet</td><td>Host-to-host across networks: IP addresses, routing</td><td>IPv4, IPv6, ICMP</td><td>Routers, L3 switches</td></tr>
<tr><td>2 Data Link</td><td rowspan="2">Network Access</td><td>Frame</td><td>Node-to-node on one link: MAC addresses, VLANs</td><td>Ethernet, 802.1Q, ARP*</td><td>Switches, NICs</td></tr>
<tr><td>1 Physical</td><td>Bits</td><td>Signals on the medium</td><td>Copper, fibre, radio</td><td>Cables, transceivers</td></tr>
</table>
<p class="small muted">*ARP maps L3 addresses to L2 addresses; it is usually placed between layers 2 and 3.</p>
<div class="callout"><b>Troubleshooting rule:</b> a higher layer cannot work if a lower layer is broken. If the cable is unplugged, DNS will “fail” too — but DNS is not the problem.</div>`,
      widgets: [readConfirm({ id: 'u02-read' })],
      question: 'A switch forwards frames using MAC addresses. Which layer is that, and what does the switch ignore?',
    },
    {
      id: 'understand', phase: 'UNDERSTAND', title: 'Encapsulation, one header at a time', workspace: false,
      content: '<p>When PC1 opens a web page, each layer wraps the one above it. Build the frame PC1 transmits.</p>',
      widgets: [encapBuilder()],
      question: 'Why can PC1 not build the Ethernet header until it has decided where the IP packet is going?',
      hints: ['Start with the thing the <b>user</b> asked for.', 'The destination MAC in the frame belongs to the <i>next hop</i>. PC1 only knows the next hop after looking at the destination IP.'],
    },
    {
      id: 'predict', phase: 'PREDICT', title: 'Where is the first failure?', workspace: false,
      content: '<p>The NOC has collected evidence on five tickets. For each, decide the <b>lowest layer</b> where the evidence shows something is wrong — that is where you would start working.</p>',
      widgets: [sorter({
        id: 'u02-triage', title: 'LAYER TRIAGE', categories: ['L1 Physical', 'L2 Data Link', 'L3 Network', 'L4 Transport', 'L7 Application'],
        items: [
          { text: '<code>ipconfig</code>: <i>Media State: Media disconnected</i>. Switch port shows <i>notconnect</i>.', answer: 'L1 Physical', why: 'No link means no signal. Nothing above can work until the medium does.', explain: 'No link = physical first.' },
          { text: 'Link is up, but the switch shows the PC’s port in VLAN 99 while the router is in VLAN 10. ARP for the gateway gets no reply.', answer: 'L2 Data Link', why: 'Signals are fine; the frame is delivered to the wrong broadcast domain. VLANs live at L2.', explain: 'Wrong VLAN = wrong L2 domain.' },
          { text: 'Gateway pings fine. <code>ping 8.8.8.8</code> → <i>Reply from 192.168.10.1: Destination net unreachable.</i>', answer: 'L3 Network', why: 'The gateway answered and explicitly said it has no route — a routing (L3) decision.', explain: 'Router has no route = L3.' },
          { text: 'Ping to the server works; <code>Test-NetConnection -Port 443</code> fails, port 80 succeeds.', answer: 'L4 Transport', why: 'IP reachability is proven. One TCP port works, another does not — that is a transport/port question.', explain: 'IP fine, one TCP port closed = L4 (service or filter).' },
          { text: 'Everything connects on TCP 443, but the browser shows a certificate name mismatch.', answer: 'L7 Application', why: 'Transport succeeded end to end; the problem is inside the application’s TLS/HTTP content.', explain: 'Connection succeeds; application content is wrong.' },
        ],
      })],
      question: 'In the VLAN ticket, pinging the gateway fails too. Why is L3 still not the right place to start?',
    },
    {
      id: 'verify', phase: 'VERIFY', title: 'See the layers on a live PC', devices: ['PC1'],
      content: `
<p>Open <b>PC1</b>’s console (Windows PowerShell). Collect evidence at several layers, then record what you see.</p>
<ul>
<li><code>ipconfig /all</code> — L2 (Physical Address) and L3 (IPv4, mask, gateway, DNS)</li>
<li><code>ping 192.168.10.1</code> — L3 reachability to the default gateway</li>
<li><code>nslookup www.example.com</code> — L7 DNS (carried by UDP)</li>
<li><code>curl http://www.example.com</code> — L7 HTTP (carried by TCP port 80)</li>
</ul>`,
      evidence: [
        { id: 'ipc', text: 'PC1: full IP configuration captured (ipconfig /all)', match: ran('PC1', 'ipconfig /all') },
        { id: 'gw', text: 'PC1: default gateway answers (ping)', match: pinged('PC1', '192.168.10.1', { success: true }) },
        { id: 'dns', text: 'PC1: DNS resolves www.example.com (nslookup)', match: resolved('PC1', 'www.example.com') },
        { id: 'web', text: 'PC1: HTTP request succeeds (curl)', match: fetched('PC1', 'www.example.com') },
      ],
      widgets: [observe({
        id: 'u02-obs', title: 'LAYER EVIDENCE', intro: 'Copy the values from PC1’s own output.',
        fields: [
          { label: 'L2 — PC1 Physical Address (MAC)', placeholder: 'xx-xx-xx-xx-xx-xx', check: (v, rt) => sameMac(v, rt.net.devices.PC1.nic.mac) },
          { label: 'L3 — PC1 IPv4 address', placeholder: 'a.b.c.d', check: (v) => v === '192.168.10.10' },
          { label: 'L3 — Default gateway', check: (v) => v === '192.168.10.1' },
          { label: 'L4 — Destination TCP port used by curl http://…', check: (v) => v === '80' || 'Which port does HTTP (not HTTPS) use by default?' },
        ],
      })],
      question: 'If <code>nslookup</code> had failed but <code>ping 93.184.216.34</code> worked, which layers would you already have proven good?',
      hints: ['The Physical Address line in <code>ipconfig /all</code> is PC1’s MAC.', 'curl to <code>http://</code> without a port number uses the protocol’s well-known port.'],
    },
    {
      id: 'continue', phase: 'CONTINUE', title: 'Carry the map with you', workspace: false,
      content: (rt) => `
<p>You just proved, from the bottom up, that PC1 works at L2 (MAC <code>${macCisco(rt.net.devices.PC1.nic.mac)}</code>), L3 (IP and gateway), L4 (TCP 80) and L7 (DNS + HTTP).</p>
<p>Every ticket from now on gets the same treatment: <b>find the lowest layer where observed behaviour differs from expected</b>, and start there.</p>`,
      question: 'Which single command on PC1 gives you evidence for both L2 and L3 at once?',
    },
  ],
};
